'use client'

import { useEffect, useRef } from 'react'
import { useSession } from 'next-auth/react'
import { trackSignupComplete } from '@/lib/analytics'
import { trpc } from '@/lib/trpc'

const SIGNUP_TRACKED_KEY = 'signup_conversion_tracked'

/**
 * Tracks signup conversions for OAuth (Google/Facebook) authentication
 * 
 * This component detects when a user has just completed OAuth signup
 * and fires the appropriate conversion tracking events to Google Analytics
 * and Google Ads.
 * 
 * Email/password signups are already tracked in the signup form,
 * but OAuth signups complete on server-side redirect, so we need
 * client-side detection.
 */
export function SignupConversionTracker() {
  const { data: session, status } = useSession()
  const hasTracked = useRef(false)
  
  // Check if user is newly created (within last 5 minutes)
  const { data: isNewUser } = trpc.user.checkIfNewSignup.useQuery(undefined, {
    enabled: status === 'authenticated' && !!session?.user?.id,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  useEffect(() => {
    // Only run when authenticated
    if (status !== 'authenticated' || !session?.user?.id) return
    
    // Check if this is a new signup
    if (!isNewUser) return
    
    // Check if we've already tracked this signup (prevent double-tracking)
    const alreadyTracked = localStorage.getItem(SIGNUP_TRACKED_KEY)
    if (alreadyTracked || hasTracked.current) return
    
    // Detect signup method from account providers
    // Check if email contains OAuth provider domains
    const email = session.user.email || ''
    let method = 'email'
    
    if (email.includes('googleusercontent.com')) {
      method = 'google'
    } else if (email.includes('facebook.com')) {
      method = 'facebook'
    }
    
    // Track the conversion
    trackSignupComplete(method)
    
    // Mark as tracked in localStorage to prevent duplicate events
    localStorage.setItem(SIGNUP_TRACKED_KEY, 'true')
    hasTracked.current = true
    
    console.log(`[Analytics] Tracked OAuth signup conversion: ${method}`)
  }, [session, status, isNewUser])
  
  // This is a tracking component, it doesn't render anything
  return null
}



